/**
 * Profile settings: validation of the editable fields, the minimal patch to save, status expiry ("clear after"),
 * timezone labels and search, and a member's local time ("3:05 PM · 3h ahead of you"). Pure: the platform supplies
 * the zone list and UTC offsets (the `TimeZones` port: web Intl, iOS TimeZone, Android ZoneId).
 */
import type { Locale, Member, MemberAvatar, UpdateProfileRequest, UserStatus } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { handleProblem, handleProblemMessage, normalizeHandle } from "./handles.ts";
import { fold } from "./search.ts";

/** Same limits as contracts settings.ts (core stays free of zod; a test keeps them equal). */
export const DISPLAY_NAME_MAX = 80;
export const FULL_NAME_MAX = 80;
export const TITLE_MAX = 100;
export const PRONOUNS_MAX = 30;
export const PHONE_MAX = 32;
export const STATUS_TEXT_MAX = 100;

export const LOCALES: Locale[] = ["en", "pt-BR"];

/** Profile photos and workspace icons (512×512 WebP from the client; PNG/JPEG where WebP can't be encoded). */
export const PROFILE_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
export const PROFILE_IMAGE_MIMES: string[] = ["image/webp", "image/png", "image/jpeg"];

/** What the profile form edits (strings as typed). */
export type ProfileDraft = {
  /** Username without "@" (as typed). */
  handle: string;
  displayName: string;
  fullName: string;
  title: string;
  pronouns: string;
  phone: string;
  phoneVisible: boolean;
  timezone: string;
  locale: Locale;
};

export type ProfileProblems = {
  handle?: MessageDescriptor;
  displayName?: MessageDescriptor;
  fullName?: MessageDescriptor;
  title?: MessageDescriptor;
  pronouns?: MessageDescriptor;
  phone?: MessageDescriptor;
  timezone?: MessageDescriptor;
};

/** The form's starting point: the saved profile; no timezone yet → the device's zone, no language → `locale`. */
export function draftFromMember(m: Member, deviceZone: string, locale: Locale): ProfileDraft {
  return {
    handle: m.handle === undefined ? "" : m.handle,
    displayName: m.displayName,
    fullName: m.fullName,
    title: m.title === undefined ? "" : m.title,
    pronouns: m.pronouns === undefined ? "" : m.pronouns,
    phone: m.phone === undefined ? "" : m.phone,
    phoneVisible: m.phoneVisible === true,
    timezone: m.timezone === undefined ? deviceZone : m.timezone,
    locale: m.locale === undefined ? locale : m.locale,
  };
}

/** Trims and collapses inner whitespace. */
export function cleanText(s: string): string {
  return s.trim().replace(/\s+/g, " ");
}

/** "+55 (11) 91234-5678": digits with optional leading +, spaces, dots, dashes and parentheses; 6–15 digits. */
export function isValidPhone(raw: string): boolean {
  const s = raw.trim();
  if (s === "" || s.length > PHONE_MAX) return false;
  let digits = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s.charAt(i);
    if (ch >= "0" && ch <= "9") digits += 1;
    else if (ch === "+") {
      if (i !== 0) return false;
    } else if (ch !== " " && ch !== "-" && ch !== "." && ch !== "(" && ch !== ")") return false;
  }
  return digits >= 6 && digits <= 15;
}

/** Field problems (`zones`: the known IANA zones; empty = don't check the zone). */
export function profileProblems(d: ProfileDraft, zones: string[]): ProfileProblems {
  const p: ProfileProblems = {};
  const handle = normalizeHandle(d.handle);
  const hp = handle === "" ? undefined : handleProblem(handle);
  if (hp !== undefined) p.handle = handleProblemMessage(hp);
  const display = cleanText(d.displayName);
  if (display === "") p.displayName = msg("profile.error.displayNameRequired");
  else if (display.length > DISPLAY_NAME_MAX) p.displayName = msg("profile.error.tooLong", { max: DISPLAY_NAME_MAX });
  if (cleanText(d.fullName).length > FULL_NAME_MAX) p.fullName = msg("profile.error.tooLong", { max: FULL_NAME_MAX });
  if (cleanText(d.title).length > TITLE_MAX) p.title = msg("profile.error.tooLong", { max: TITLE_MAX });
  if (cleanText(d.pronouns).length > PRONOUNS_MAX) p.pronouns = msg("profile.error.tooLong", { max: PRONOUNS_MAX });
  if (d.phone.trim() !== "" && !isValidPhone(d.phone)) p.phone = msg("profile.error.phone");
  if (d.timezone !== "" && zones.length > 0 && !zones.includes(d.timezone)) p.timezone = msg("profile.error.timezone");
  return p;
}

export function hasProblems(p: ProfileProblems): boolean {
  return p.handle !== undefined || p.displayName !== undefined || p.fullName !== undefined || p.title !== undefined || p.pronouns !== undefined || p.phone !== undefined || p.timezone !== undefined;
}

/**
 * Only what changed between the saved profile (`saved`, from draftFromMember) and the form (cleaned); empty strings
 * clear. `{}` = nothing to save.
 */
export function profilePatch(saved: ProfileDraft, d: ProfileDraft): UpdateProfileRequest {
  const req: UpdateProfileRequest = {};
  const handle = normalizeHandle(d.handle);
  if (handle !== "" && handle !== saved.handle) req.handle = handle;
  const display = cleanText(d.displayName);
  if (display !== saved.displayName) req.displayName = display;
  const full = cleanText(d.fullName);
  const nextDisplay = req.displayName === undefined ? saved.displayName : req.displayName;
  if (full !== saved.fullName && !(full === "" && saved.fullName === nextDisplay)) req.fullName = full;
  const title = cleanText(d.title);
  if (title !== saved.title) req.title = title;
  const pronouns = cleanText(d.pronouns);
  if (pronouns !== saved.pronouns) req.pronouns = pronouns;
  const phone = cleanText(d.phone);
  if (phone !== saved.phone) req.phone = phone;
  if (d.phoneVisible !== saved.phoneVisible) req.phoneVisible = d.phoneVisible;
  if (d.timezone !== saved.timezone) req.timezone = d.timezone;
  if (d.locale !== saved.locale) req.locale = d.locale;
  return req;
}

/**
 * Auto-detect: what to save silently the first time settings open when the profile has no timezone or language yet
 * (the device's zone, the app's language). `{}` when both are set.
 */
export function missingDefaults(m: Member, deviceZone: string, locale: Locale): UpdateProfileRequest {
  const req: UpdateProfileRequest = {};
  if (m.timezone === undefined && deviceZone !== "") req.timezone = deviceZone;
  if (m.locale === undefined) req.locale = locale;
  return req;
}

export function isEmptyPatch(req: UpdateProfileRequest): boolean {
  return Object.keys(req).length === 0;
}

/**
 * The member as it will look once `req` is saved (optimistic UI, and the mock server). The photo isn't known until the
 * server answers (`avatarFileId`), so only `removeAvatar` changes it here.
 */
export function applyProfilePatch(m: Member, req: UpdateProfileRequest): Member {
  const out: Member = { ...m };
  if (req.displayName !== undefined) out.displayName = cleanText(req.displayName);
  if (req.fullName !== undefined) out.fullName = cleanText(req.fullName) === "" ? out.displayName : cleanText(req.fullName);
  const text = (key: "title" | "pronouns" | "phone" | "timezone", v: string | undefined) => {
    if (v === undefined) return;
    const c = cleanText(v);
    if (c === "") delete out[key];
    else out[key] = c;
  };
  text("title", req.title);
  text("pronouns", req.pronouns);
  text("phone", req.phone);
  text("timezone", req.timezone);
  if (req.phoneVisible !== undefined) out.phoneVisible = req.phoneVisible;
  if (req.locale !== undefined) out.locale = req.locale;
  if (req.status !== undefined) {
    if (req.status.emoji === "" && req.status.text.trim() === "") delete out.status;
    else out.status = { ...req.status, text: cleanText(req.status.text) };
  }
  if (req.handle !== undefined) out.handle = normalizeHandle(req.handle);
  if (req.removeAvatar === true) {
    delete out.avatarUrl;
    delete out.avatar;
  }
  if (req.avatar !== undefined && req.avatar.kind !== "photo") {
    delete out.avatarUrl;
    out.avatar = req.avatar;
  }
  return out;
}

// ---------- Status ----------

/** "Clear after" choices. */
export type StatusDuration = "never" | "30m" | "1h" | "4h" | "today" | "week";
export const STATUS_DURATIONS: StatusDuration[] = ["never", "30m", "1h", "4h", "today", "week"];

const MINUTE = 60_000;
const DAY = 86_400_000;

/** When a status set now should clear (`tzOffsetMinutes`: my local offset; "today" = my next local midnight). */
export function statusExpiresAt(duration: StatusDuration, now: number, tzOffsetMinutes: number): number | undefined {
  switch (duration) {
    case "never":
      return undefined;
    case "30m":
      return now + 30 * MINUTE;
    case "1h":
      return now + 60 * MINUTE;
    case "4h":
      return now + 240 * MINUTE;
    case "today": {
      const local = now + tzOffsetMinutes * MINUTE;
      return Math.floor(local / DAY) * DAY + DAY - tzOffsetMinutes * MINUTE;
    }
    case "week":
      return now + 7 * DAY;
  }
}

/**
 * The status to show (absent, empty or expired → undefined). Shown as the emoji after the person's name (sidebar DMs,
 * message authors, mentions, member lists, DM header) with a tooltip: emoji, text and `statusUntilLabel`.
 */
export function activeStatus(m: { status?: UserStatus }, now: number): UserStatus | undefined {
  const s = m.status;
  if (s === undefined || (s.emoji === "" && s.text === "")) return undefined;
  if (s.expiresAt !== undefined && s.expiresAt <= now) return undefined;
  return s;
}

/**
 * The soonest moment after `now` when someone's status runs out, for one timer that re-renders status emoji exactly
 * then (expired statuses are hidden by `activeStatus`; the server clears them too). Undefined when nothing expires.
 */
export function nextStatusExpiry(members: Record<string, { status?: UserStatus }>, now: number): number | undefined {
  let next: number | undefined;
  for (const m of Object.values(members)) {
    const s = activeStatus(m, now);
    const t = s === undefined ? undefined : s.expiresAt;
    if (t !== undefined && (next === undefined || t < next)) next = t;
  }
  return next;
}

/**
 * When a status clears, as the tooltip's "Until …" line shows it, in the viewer's time zone: the same calendar day →
 * the time ("Until 7:59 PM"); the next day → "Until tomorrow"; within a week → the weekday ("Until Friday", 0 =
 * Sunday); later → the date (`sameYear` false → with the year). Months are 1–12.
 */
export type StatusUntil =
  | { kind: "time"; hour: number; minute: number }
  | { kind: "tomorrow" }
  | { kind: "weekday"; weekday: number }
  | { kind: "date"; year: number; month: number; day: number; sameYear: boolean };

/** Calendar date and wall clock of an instant in a time zone (an unknown zone reads as UTC). */
export function zonedDate(at: number, timeZone: string): { year: number; month: number; day: number; hour: number; minute: number; weekday: number } {
  const opts: Intl.DateTimeFormatOptions = { year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", hourCycle: "h23", weekday: "short" };
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", { ...opts, timeZone }).formatToParts(at);
  } catch {
    parts = new Intl.DateTimeFormat("en-US", { ...opts, timeZone: "UTC" }).formatToParts(at);
  }
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "0";
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(get("weekday"));
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hour: Number(get("hour")) % 24,
    minute: Number(get("minute")),
    weekday: weekday < 0 ? 0 : weekday,
  };
}

/** The tooltip's "Until …" for a status (`timeZone`: the viewer's). Undefined when it never clears or already has. */
export function statusUntil(expiresAt: number | undefined, now: number, timeZone: string): StatusUntil | undefined {
  if (expiresAt === undefined || expiresAt <= now) return undefined;
  const a = zonedDate(now, timeZone);
  const b = zonedDate(expiresAt, timeZone);
  const days = Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / DAY);
  if (days <= 0) return { kind: "time", hour: b.hour, minute: b.minute };
  if (days === 1) return { kind: "tomorrow" };
  if (days < 7) return { kind: "weekday", weekday: b.weekday };
  return { kind: "date", year: b.year, month: b.month, day: b.day, sameYear: a.year === b.year };
}

/** The status tooltip's clock: en "7:59 PM"; pt-BR "19h59". */
export function statusClock(hour: number, minute: number, locale: Locale): string {
  return locale === "pt-BR" ? `${hour}h${pad2(minute)}` : formatClock(hour, minute, locale);
}

const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"] as const;

/**
 * The "Until …" line: "Until 7:59 PM" / "Até 19h59", "Until tomorrow", "Until Friday" / "Até sexta", "Until Oct 30",
 * and with another year "Until 1/5/2027" / "Até 05/01/2027". `inline`: the lower-case form that follows the status
 * text in the DM header ("In a meeting · until 7:59 PM").
 */
export function statusUntilLabel(u: StatusUntil, locale: Locale, inline: boolean = false): MessageDescriptor {
  const base = inline ? "status.untilInline" : "status.until";
  switch (u.kind) {
    case "time":
      return msg(`${base}.time`, { time: statusClock(u.hour, u.minute, locale) });
    case "tomorrow":
      return msg(`${base}.tomorrow`);
    case "weekday":
      return msg(`${base}.weekday.${WEEKDAYS[((u.weekday % 7) + 7) % 7]!}`);
    case "date":
      if (u.sameYear) return msg(`${base}.month.${MONTHS[Math.min(Math.max(u.month, 1), 12) - 1]!}`, { day: u.day });
      return msg(`${base}.date`, { date: locale === "pt-BR" ? `${pad2(u.day)}/${pad2(u.month)}/${u.year}` : `${u.month}/${u.day}/${u.year}` });
  }
}

/** Both at once for a member's status tooltip; undefined = no "Until" line (no status, or it never clears). */
export function statusUntilText(m: { status?: UserStatus }, now: number, timeZone: string, locale: Locale, inline: boolean = false): MessageDescriptor | undefined {
  const s = activeStatus(m, now);
  const u = s === undefined ? undefined : statusUntil(s.expiresAt, now, timeZone);
  return u === undefined ? undefined : statusUntilLabel(u, locale, inline);
}

/** Slack's emoji for a status set with text but no emoji (:speech_balloon:). */
export const STATUS_DEFAULT_EMOJI = "💬";

/**
 * Everything a status display needs, in the viewer's zone and language: `emoji` (the default one when only text was
 * set), `text` ("" for an emoji-only status), `until` for the tooltip / profile ("Until 7:59 PM"), `untilInline` for
 * the DM header ("… · until 7:59 PM"), both absent when it never clears, and `label`, the emoji's accessible name
 * ("Status: Out sick", or just "Status"). Undefined: no status to show (none, empty or expired).
 */
export type StatusDisplay = { emoji: string; text: string; until?: MessageDescriptor; untilInline?: MessageDescriptor; label: MessageDescriptor };

export function statusDisplay(m: { status?: UserStatus }, now: number, timeZone: string, locale: Locale): StatusDisplay | undefined {
  const s = activeStatus(m, now);
  if (s === undefined) return undefined;
  const text = cleanText(s.text);
  const out: StatusDisplay = {
    emoji: s.emoji === "" ? STATUS_DEFAULT_EMOJI : s.emoji,
    text,
    label: text === "" ? msg("status.a11y.emojiOnly") : msg("status.a11y", { text }),
  };
  const u = statusUntil(s.expiresAt, now, timeZone);
  if (u !== undefined) {
    out.until = statusUntilLabel(u, locale);
    out.untilInline = statusUntilLabel(u, locale, true);
  }
  return out;
}

export function statusProblem(text: string): MessageDescriptor | undefined {
  return cleanText(text).length > STATUS_TEXT_MAX ? msg("profile.error.tooLong", { max: STATUS_TEXT_MAX }) : undefined;
}

export type StatusPreset = { emoji: string; label: MessageDescriptor; duration: StatusDuration };

/** One-tap statuses (the label is translated into the status text when chosen). */
export const STATUS_PRESETS: StatusPreset[] = [
  { emoji: "📅", label: msg("profile.status.preset.meeting"), duration: "1h" },
  { emoji: "🚌", label: msg("profile.status.preset.commuting"), duration: "30m" },
  { emoji: "🤒", label: msg("profile.status.preset.sick"), duration: "today" },
  { emoji: "🌴", label: msg("profile.status.preset.vacation"), duration: "never" },
  { emoji: "🏡", label: msg("profile.status.preset.remote"), duration: "today" },
];

export function durationLabel(d: StatusDuration): MessageDescriptor {
  switch (d) {
    case "never":
      return msg("profile.status.clear.never");
    case "30m":
      return msg("profile.status.clear.halfHour");
    case "1h":
      return msg("profile.status.clear.hour");
    case "4h":
      return msg("profile.status.clear.fourHours");
    case "today":
      return msg("profile.status.clear.today");
    case "week":
      return msg("profile.status.clear.week");
  }
}

// ---------- Time zones & local time ----------

/** "America/Argentina/Buenos_Aires" → "Buenos Aires"; "UTC" → "UTC". */
export function zoneCity(zone: string): string {
  const parts = zone.split("/");
  return parts[parts.length - 1]!.replace(/_/g, " ");
}

/** "America/Sao_Paulo" → "America"; "UTC" → "". */
export function zoneRegion(zone: string): string {
  const slash = zone.indexOf("/");
  return slash < 0 ? "" : zone.slice(0, slash).replace(/_/g, " ");
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** -180 → "UTC-03:00", 330 → "UTC+05:30", 0 → "UTC". */
export function formatOffset(offsetMinutes: number): string {
  if (offsetMinutes === 0) return "UTC";
  const abs = Math.abs(offsetMinutes);
  return `UTC${offsetMinutes < 0 ? "-" : "+"}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

/** Zones matching `query` (city, region or id; accent-insensitive), city-prefix matches first, then A→Z. */
export function searchZones(zones: string[], query: string, limit: number): string[] {
  const q = fold(query).trim().replace(/\s+/g, " ");
  const scored: { zone: string; score: number; city: string }[] = [];
  for (const z of zones) {
    const city = fold(zoneCity(z));
    const id = fold(z.replace(/_/g, " "));
    let score = 0;
    if (q === "") score = 1;
    else if (city.startsWith(q)) score = 3;
    else if (city.includes(q)) score = 2;
    else if (id.includes(q)) score = 1;
    if (score > 0) scored.push({ zone: z, score, city });
  }
  scored.sort((a, b) => b.score - a.score || (a.city < b.city ? -1 : a.city > b.city ? 1 : a.zone < b.zone ? -1 : a.zone > b.zone ? 1 : 0));
  return scored.slice(0, Math.max(0, limit)).map((s) => s.zone);
}

/** Wall-clock hour and minute at `now` in a zone `offsetMinutes` from UTC. */
export function localTimeParts(now: number, offsetMinutes: number): { hour: number; minute: number } {
  const minutes = Math.floor((now + offsetMinutes * MINUTE) / MINUTE);
  const ofDay = ((minutes % 1440) + 1440) % 1440;
  return { hour: Math.floor(ofDay / 60), minute: ofDay % 60 };
}

/** en: "3:05 PM"; pt-BR: "15:05". */
export function formatClock(hour: number, minute: number, locale: Locale): string {
  if (locale === "pt-BR") return `${pad2(hour)}:${pad2(minute)}`;
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}:${pad2(minute)} ${hour < 12 ? "AM" : "PM"}`;
}

/** "3h", "5h 30m", "45m". */
function spanLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/**
 * A member's local time for their profile card: "3:05 PM" and how it compares with mine ("3h ahead of you",
 * absent when it's the same).
 */
export function localTimeLabel(now: number, theirOffset: number, myOffset: number, locale: Locale): { time: string; difference?: MessageDescriptor } {
  const { hour, minute } = localTimeParts(now, theirOffset);
  const out: { time: string; difference?: MessageDescriptor } = { time: formatClock(hour, minute, locale) };
  const diff = theirOffset - myOffset;
  if (diff > 0) out.difference = msg("profile.time.ahead", { span: spanLabel(diff) });
  else if (diff < 0) out.difference = msg("profile.time.behind", { span: spanLabel(-diff) });
  return out;
}

export function localeLabel(l: Locale): MessageDescriptor {
  return l === "pt-BR" ? msg("language.ptBR") : msg("language.en");
}

// ---------- Avatars ----------

/** The tones a non-photo avatar can use (`avatar.1`…`avatar.8`). */
export const AVATAR_TONES: number[] = [1, 2, 3, 4, 5, 6, 7, 8];

/** A member's avatar: the explicit choice, else a photo from `avatarUrl` (older servers), else none (initials). */
export function memberAvatar(m: Member): MemberAvatar | undefined {
  if (m.avatar !== undefined) return m.avatar;
  return m.avatarUrl === undefined ? undefined : { kind: "photo", url: m.avatarUrl };
}

/** What the Avatar primitive draws: a photo `src`, or initials / an emoji on `tone` (absent: the name's tone). */
export type AvatarLook = { src?: string; tone?: number; emoji?: string };

export function avatarLook(m: Member): AvatarLook {
  const a = memberAvatar(m);
  if (a === undefined) return {};
  switch (a.kind) {
    case "photo":
      return { src: a.url };
    case "initials":
      return { tone: a.color };
    case "emoji":
      return { tone: a.color, emoji: a.emoji };
  }
}
